import { useQuery } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Chrome } from '@/components/layout/Chrome';
import { api } from '@/lib/api';

interface Detail {
  _id: string;
  status: string;
  personal: Record<string, unknown>;
  agency: Record<string, unknown>;
  bank: Record<string, unknown>;
  occupation: Record<string, unknown>;
  utr?: Record<string, unknown>;
  payment: Record<string, unknown>;
  cisAssessment?: Record<string, unknown>;
  cisVerification?: Record<string, unknown>;
  submittedAt: string;
  _signedUrls: Record<string, string>;
}

const Section = ({ title, data }: { title: string; data: unknown }) => (
  <section className="mt-6 rounded border border-ink-100 p-4">
    <h2 className="text-xs font-bold uppercase tracking-wider text-navy">{title}</h2>
    <pre className="mt-2 whitespace-pre-wrap break-words text-[12px] text-ink-700">
      {JSON.stringify(data, null, 2)}
    </pre>
  </section>
);

const TREATMENT_LABEL: Record<string, string> = {
  gross: 'Gross (0%)',
  net: 'Net / standard (20%)',
  higher: 'Higher (30%)',
};

// Formatted CIS HMRC verification result (rather than a raw JSON blob), so admins can read
// the outcome at a glance.
const CisVerificationSection = ({ v }: { v: Record<string, unknown> }) => {
  const matched = v.matchStatus === 'matched';
  const treatment = String(v.taxTreatment ?? '');
  const verifiedAt = v.verifiedAt ? new Date(String(v.verifiedAt)).toLocaleString() : '—';
  const rows: Array<[string, ReactNode]> = [
    [
      'Status',
      <span className={matched ? 'font-bold text-teal-dark' : 'font-bold text-amber-600'}>
        {matched ? 'Matched' : 'Not matched'}
      </span>,
    ],
    ['Verification number', String(v.verificationNumber || '—')],
    ['Tax treatment', TREATMENT_LABEL[treatment] ?? treatment ?? '—'],
    ['Verified at', verifiedAt],
    ['Source', v.mode === 'live' ? 'HMRC (live)' : 'Stub (test)'],
  ];
  return (
    <section className="mt-6 rounded border border-ink-100 p-4">
      <h2 className="text-xs font-bold uppercase tracking-wider text-navy">CIS HMRC Verification</h2>
      <dl className="mt-2 grid grid-cols-[180px_1fr] gap-y-1.5 text-[13px]">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-ink-500">{label}</dt>
            <dd className="text-ink-800">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export const AdminDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useQuery({
    queryKey: ['registration', id],
    queryFn: async () => (await api.get<Detail>(`/registrations/${id}`)).data,
    enabled: !!id,
  });

  return (
    <Chrome>
      <div className="min-h-[60vh] bg-ink-50 px-6 py-9">
        <div className="mx-auto max-w-4xl rounded-lg bg-white p-6 shadow-card">
          {isLoading && <p className="text-sm text-ink-500">Loading…</p>}
          {data && (
            <>
              <h1 className="text-lg font-black uppercase tracking-widest text-navy">
                Submission {data._id}
              </h1>
              <p className="text-xs text-ink-500">
                Submitted {new Date(data.submittedAt).toLocaleString()} · Status {data.status}
              </p>
              <Section title="Personal Details" data={data.personal} />
              <Section title="Agency" data={data.agency} />
              <Section title="Bank" data={data.bank} />
              <Section title="Occupation" data={data.occupation} />
              {data.utr && <Section title="UTR" data={data.utr} />}
              <Section title="Payment" data={data.payment} />
              {data.cisAssessment && (
                <Section title="CIS Assessment" data={data.cisAssessment} />
              )}
              {data.cisVerification && <CisVerificationSection v={data.cisVerification} />}
              <section className="mt-6 rounded border border-ink-100 p-4">
                <h2 className="text-xs font-bold uppercase tracking-wider text-navy">Documents</h2>
                <ul className="mt-2 space-y-2 text-sm">
                  {Object.entries(data._signedUrls).map(([kind, url]) => (
                    <li key={kind}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-teal-dark underline"
                      >
                        {kind}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </div>
      </div>
    </Chrome>
  );
};
